import type { ApolloCache } from "@apollo/client";
import { isReference } from "@apollo/client/utilities";

import { getTagsForEntity as getTagsForEntityQuery } from "./queries/tags.graphql";
import { mergeEntityTagCacheRecord } from "./TagsEditor.util";
import { readCacheField } from "./tagsEditorApolloCache";

type CachedEntityTagRecord = {
  id?: string;
  entityTagAttributes?: Record<string, unknown> | null;
  tag?: Record<string, unknown>;
};

type GetTagsForEntityData = {
  tags?: CachedEntityTagRecord[];
};

export type EntityTagAttributesCacheFields = {
  entityTagAssociationId: string;
  entityTagAttributes: Record<string, unknown>;
  entityId?: string;
  entityType?: string;
  /** Tag types used by mutation/cache helpers (often selection config). */
  tagTypes?: string[];
  /** Tag types on the live getTagsForEntity query (often display config). */
  tagsQueryTagTypes?: string[];
};

/** Dedupe tag-type lists so we write the same entity-tag patch to every active query shape. */
export const uniqueTagTypesLists = (
  ...lists: Array<string[] | undefined | null>
): string[][] => {
  const seen = new Set<string>();
  const result: string[][] = [];

  for (const list of lists) {
    if (!list?.length) {
      continue;
    }
    const key = [...list].sort().join("\0");
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(list);
  }

  return result;
};

const patchTagsForEntityQuery = (
  cache: ApolloCache,
  variables: {
    entityId: string;
    tagTypes: string[];
    entityType?: string;
  },
  entityTagAssociationId: string,
  patch: Partial<CachedEntityTagRecord>
): void => {
  let cached: GetTagsForEntityData | null = null;
  try {
    cached = cache.readQuery<GetTagsForEntityData>({
      query: getTagsForEntityQuery,
      variables,
    });
  } catch {
    cached = null;
  }

  if (!cached?.tags?.length) {
    return;
  }

  let changed = false;
  const tags = cached.tags.map((entityTag) => {
    const cachedEntityTagId = readCacheField<string>(cache, "id", entityTag);
    if (cachedEntityTagId !== entityTagAssociationId) {
      return entityTag;
    }
    changed = true;
    if (isReference(entityTag)) {
      return entityTag;
    }
    return mergeEntityTagCacheRecord(entityTag, patch);
  });

  if (!changed) {
    return;
  }

  cache.writeQuery({
    query: getTagsForEntityQuery,
    variables,
    data: { tags },
  });
};

/**
 * Persist unique entity-tag attributes into Apollo after updateEntityTag.
 * Writes every query variant the TagsEditor container may subscribe to.
 */
export const updateEntityTagAttributesInCache = (
  cache: ApolloCache,
  {
    entityTagAssociationId,
    entityTagAttributes,
    entityId,
    entityType,
    tagTypes,
    tagsQueryTagTypes,
  }: EntityTagAttributesCacheFields
): void => {
  const entityCacheId = cache.identify({
    __typename: "EntityTag",
    id: entityTagAssociationId,
  });

  if (entityCacheId) {
    cache.modify({
      id: entityCacheId,
      fields: {
        entityTagAttributes: () => entityTagAttributes,
      },
    });
  }

  if (!entityId) {
    return;
  }

  const patch = { entityTagAttributes };
  for (const tagTypesList of uniqueTagTypesLists(tagsQueryTagTypes, tagTypes)) {
    patchTagsForEntityQuery(
      cache,
      {
        entityId,
        tagTypes: tagTypesList,
        ...(entityType ? { entityType } : {}),
      },
      entityTagAssociationId,
      patch
    );
  }
};
